import { gsap } from 'gsap';
import EventBus from 'eventing-bus';
import { useDispatch, useSelector } from 'react-redux';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useRef, useState, useEffect } from 'react';
import { Link, withRouter, useLocation } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import './index.css';
import logo from '../../static/img/logo.svg';
import { resetPassword } from '../../store/Auth';
import { getBranding } from '../../store/Settings';

gsap.registerPlugin(SplitText, ScrollTrigger);
const initialState = { code: '', password: '', confirmPassword: '' }
const ResetPassword = ({ history }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);
  const { currentBranding } = useSelector(state => state['Settings']);
  
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [key, setEmailCode] = useState(['', '', '', '', '', '']);
  const emailInputRefs = Array.from({ length: 6 }, () => useRef(null));
  const togglePasswordVisibility = field => setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));

  useEffect(() => { document.title = `Reset Password | ${currentBranding?.['isCustom'] && currentBranding?.['agencyName'] ? currentBranding['agencyName'] : 'Sales AI'}` }, [currentBranding]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (code) {
      const newKey = ['', '', '', '', '', ''];
      const codeDigits = code.slice(0, 6).split('');
      codeDigits.forEach((digit, index) => { if (index < 6 && /^\d$/.test(digit)) newKey[index] = digit })
      setEmailCode(newKey);

      const firstEmptyIndex = newKey.findIndex(digit => digit === '');
      const focusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : 5;
      setTimeout(() => { if (emailInputRefs[focusIndex]?.current) emailInputRefs[focusIndex].current.focus() }, 100)
    }
  }, [location.search])

  const handleEmailInputChange = (index, value) => {
    if (value.length > 1) value = value.slice(value.length - 1)
    setEmailCode(st => st.map((_, idx) => idx === index ? value : _))
    if (value.length === 1 && index < 5) emailInputRefs[index + 1].current.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newKey = [...key]
      const digits = pastedData.split('')
      digits.forEach((digit, index) => {
        if (index < emailInputRefs.length) {
          newKey[index] = digit
          if (index === digits.length - 1 && index < emailInputRefs.length - 1) emailInputRefs[index + 1].current.focus()
        }
      })
      setEmailCode(newKey)
    }
  }

  const handleChange = e => setState(st => ({ ...st, [e['target'] && e['target']['name']]: e['target'] && e['target']['value'] }))
  const handleLogin = () => {
    if (state['password'] != state['confirmPassword']) return EventBus.publish('error', 'Passwords does not match')

    setLoading(true)
    dispatch(resetPassword({
      formData: { password: state['password'], key: +`${key.join('')}` },
      successCallback: () => {
        setLoading(false)
        setState(initialState)
        history.push('/login')
      }, failCallback: () => setLoading(false)
    }))
  }

  useEffect(() => {
      // Reset any previous SplitText instances
      splitParagraphs.current.forEach((split) => split?.revert?.());
      splitParagraphs.current = [];
  
      // Animate each heading
      headingRefs.current.forEach((el, i) => {
        gsap.fromTo(
          el,
          { opacity: 0, filter: "blur(10px)", y: -20 },
          {
            opacity: 1,
            filter: "blur(0px)",
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: i * 0.3,
          }
        );
      });
  
      // Animate each paragraph with SplitText
      paragraphRefs.current.forEach((el, i) => {
        const split = new SplitText(el, { type: "chars" });
        splitParagraphs.current.push(split);
  
        gsap.fromTo(
          split.chars,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            stagger: 0.02,
            ease: "power2.out",
            delay: (headingRefs.current.length + i) * 0.1,
          }
        );
      });
    }, []);
  

  return (
    <div className='sa-auth-page'>
      {/* ── Left Hero Background ── */}
      <div className='sa-hero-bg'>
        <div className='sa-mesh-overlay'></div>
        <div className='sa-grid-lines'></div>
        <div className='sa-particle sa-particle-1'></div>
        <div className='sa-particle sa-particle-2'></div>
        <div className='sa-particle sa-particle-3'></div>
        <div className='sa-particle sa-particle-4'></div>
        <div className='sa-particle sa-particle-5'></div>
        <div className='sa-particle sa-particle-6'></div>
        <div className='sa-particle sa-particle-7'></div>
        <div className='sa-particle sa-particle-8'></div>
        <div className='sa-shape sa-shape-ring'></div>
        <div className='sa-shape sa-shape-hex'></div>
        <div className='sa-shape sa-shape-tri'></div>
      </div>

      <div className='sa-layout'>
        {/* ── Left Content ── */}
        <div className='sa-left-content'>
          <div className='sa-brand'>
            <div className='sa-brand-logo'>
              {currentBranding?.['isCustom'] && currentBranding?.['logo'] ? (
                <img src={currentBranding['logo']} alt={currentBranding['agencyName'] || 'Logo'} style={{ height: '42px', maxWidth: '160px', objectFit: 'contain' }} />
              ) : (
                <svg width="42" height="42" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="rpO1" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#e8709a" stopOpacity="0"/><stop offset="40%" stopColor="#ff9cc0"/><stop offset="60%" stopColor="#ff9cc0"/><stop offset="100%" stopColor="#e8709a" stopOpacity="0"/></linearGradient>
                    <linearGradient id="rpO2" x1="0" y1="0.5" x2="1" y2="0.5"><stop offset="0%" stopColor="#c44070" stopOpacity="0"/><stop offset="40%" stopColor="#e8709a"/><stop offset="60%" stopColor="#e8709a"/><stop offset="100%" stopColor="#c44070" stopOpacity="0"/></linearGradient>
                    <radialGradient id="rpN" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#ffffff"/><stop offset="50%" stopColor="#ffb8d0"/><stop offset="100%" stopColor="#d4547a"/></radialGradient>
                    <filter id="rpG" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                  </defs>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(-40 27 27)" fill="none" stroke="url(#rpO1)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(40 27 27)" fill="none" stroke="url(#rpO2)" strokeWidth="1.8"/>
                  <ellipse cx="27" cy="27" rx="19" ry="7.5" transform="rotate(90 27 27)" fill="none" stroke="url(#rpO1)" strokeWidth="1.2" opacity="0.7"/>
                  <circle cx="8.5" cy="27" r="3" fill="#ff9cc0" filter="url(#rpG)"/>
                  <circle cx="44.5" cy="22" r="2.4" fill="#e8709a" filter="url(#rpG)"/>
                  <circle cx="30" cy="8.5" r="2.4" fill="#c44070" filter="url(#rpG)"/>
                  <circle cx="27" cy="27" r="6.5" fill="rgba(84,21,48,0.5)" filter="url(#rpG)"/>
                  <circle cx="27" cy="27" r="5" fill="url(#rpN)"/>
                  <circle cx="27" cy="27" r="2.5" fill="white" opacity="0.95"/>
                </svg>
              )}
            </div>
            <span className='sa-brand-name'>
              {currentBranding?.['isCustom'] && currentBranding?.['agencyName'] ? currentBranding['agencyName'] : 'Sales AI'}
            </span>
          </div>
          <div className='sa-hero-badge'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Secure Verification
          </div>
          <h1 className='sa-hero-headline'>Almost<br />there — verify<br />& reset.</h1>
          <p className='sa-hero-subtitle'>Verify your identity with the 6-digit code sent to your email, then create a new password.</p>

          <div className='sa-stats-grid'>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>3.2x</span>
              <span className='sa-stat-label'>Conversion</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>12k+</span>
              <span className='sa-stat-label'>Active Sellers</span>
            </div>
            <div className='sa-stat-card'>
              <span className='sa-stat-number'>89%</span>
              <span className='sa-stat-label'>Time Saved</span>
            </div>
          </div>

          <div className='sa-testimonial'>
            <p className='sa-testimonial-text'>"SalesAI transformed our pipeline. We close 3x more deals with half the manual work. The AI follow-ups are a game-changer."</p>
            <div className='sa-testimonial-author'>
              <div className='sa-testimonial-avatar'>SK</div>
              <div>
                <strong>Sarah Kim</strong>
                <span>VP Sales, TechCorp</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Content ── */}
        <div className='sa-right-content'>
          <div className='sa-auth-card'>
            <div className='sa-card-icon'>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--sa-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11.25 14.25 15 9"/></svg>
            </div>
            <h2 className='sa-card-title'>Confirm Code</h2>
            <p className='sa-card-subtitle'>Enter the 6-digit code sent to your email and create a new password.</p>

            <ValidatorForm className='sa-form' onSubmit={handleLogin}>
              <div className='sa-field'>
                <label className='sa-label'>Verification Code</label>
                <div className='sa-otp-group'>
                  {emailInputRefs.map((ref, index) => (
                    <TextValidator
                      min={0}
                      fullWidth
                      required
                      key={index}
                      type='number'
                      inputRef={ref}
                      margin='dense'
                      placeholder=''
                      variant='outlined'
                      value={key[index]}
                      className='sa-input sa-otp-input'
                      name={`EmailCodeEnter${index}`}
                      onPaste={index === 0 ? handlePaste : undefined}
                      onChange={e => handleEmailInputChange(index, e['target']['value'])}
                    />
                  ))}
                </div>
              </div>

              <div className='sa-field sa-field-password'>
                <label className='sa-label'>New Password</label>
                <TextValidator
                  required
                  margin='dense'
                  name='password'
                  variant='outlined'
                  onChange={handleChange}
                  value={state['password']}
                  className='sa-input'
                  placeholder='Enter new password'
                  type={showPassword['password'] ? 'text' : 'password'}
                />
                <button type='button' className='sa-toggle-password' onClick={() => togglePasswordVisibility('password')}>
                  <i className={`fa ${showPassword['password'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <div className='sa-field sa-field-password'>
                <label className='sa-label'>Confirm Password</label>
                <TextValidator
                  required
                  margin='dense'
                  variant='outlined'
                  name='confirmPassword'
                  onChange={handleChange}
                  className='sa-input'
                  value={state['confirmPassword']}
                  placeholder='Confirm new password'
                  type={showPassword['confirmPassword'] ? 'text' : 'password'}
                />
                <button type='button' className='sa-toggle-password' onClick={() => togglePasswordVisibility('confirmPassword')}>
                  <i className={`fa ${showPassword['confirmPassword'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <button type='submit' className='sa-btn-primary' disabled={loading}>
                {!loading ? (
                  <>
                    Reset Password
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </>
                ) : (
                  <i className='fa fa-spinner fa-spin'></i>
                )}
              </button>
            </ValidatorForm>

            <p className='sa-switch-text'>
              <Link to='/login'>Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>

    /* ── OLD UI (commented out) ──
    <div className='login-page'>
      <div className='logo-area'>
        <></>
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area forgot-password-img'>
              ...
            </div>
          </div>
          <div className='col-lg-5 offset-lg-1 col-md-6 col-sm-12 login-area'>
            <div className='login-form'>
              ... (Confirm Code form with OTP inputs, new password, confirm password) ...
            </div>
          </div>
        </div>
      </div>
    </div>
    */
  );
};


export default (withRouter(ResetPassword));